import { httpClient } from '@bs/core'
import type {
  BrandLogoResponse,
  CampaignAudience,
  CampaignAudiencePreview,
  CreateCampaignInput,
  CreateFlowInput,
  LoyaltyBrand,
  LoyaltyCampaign,
  CampaignReportActivityResponse,
  CampaignReportActivityType,
  CampaignReportOverview,
  CampaignReportRecipientDetail,
  CampaignReportRecipientsParams,
  CampaignReportRecipientsResponse,
  CampaignReportSummary,
  LoyaltyCampaignHistoryResponse,
  LoyaltyFlow,
  LoyaltyFlowCustomerDetail,
  LoyaltyFlowCustomerIdentity,
  LoyaltyFlowCustomersParams,
  LoyaltyFlowCustomersResponse,
  LoyaltyFlowHistoryParams,
  LoyaltyFlowHistoryResponse,
  PreviewCampaignInput,
  PreviewCampaignResult,
  TestSendCampaignInput,
  TestSendCampaignResult,
  UpdateCampaignInput,
  UpdateFlowInput,
} from '../../types/loyaltyMarketing'

// httpClient.json.* unwraps res.data.data → the resolved value is the inner
// `data` payload directly (SuccessResponse envelope already stripped). The
// org + store headers are injected automatically by `orgAndStoreHeader`, so
// these calls resolve to the same be_api the rest of the loyalty surface uses.
// The Email-Marketing wire shape already matches the camel/snake type defs in
// `types/loyaltyMarketing.ts`, so — unlike the members/overview endpoints —
// no snake↔camel mapper is needed; payloads pass through untouched.

// ── URL builders ──────────────────────────────────────────────────────────────

const loyaltyBase = (storeId: string) => `/stores/${storeId}/loyalty`
const campaignsUrl = (storeId: string) => `${loyaltyBase(storeId)}/campaigns`
const flowsUrl = (storeId: string) => `${loyaltyBase(storeId)}/flows`

// ── Campaigns ────────────────────────────────────────────────────────────────

export const getLoyaltyCampaigns = async (
  storeId: string,
): Promise<LoyaltyCampaign[]> => {
  const raw = (await httpClient.json.get(campaignsUrl(storeId))) as {
    campaigns?: LoyaltyCampaign[]
  } | null
  return raw?.campaigns ?? []
}

export const createLoyaltyCampaign = async (
  storeId: string,
  input: CreateCampaignInput,
): Promise<LoyaltyCampaign> => {
  const raw = (await httpClient.json.post(campaignsUrl(storeId), input)) as {
    campaign: LoyaltyCampaign
  }
  return raw.campaign
}

// Live audience counts for the builder (nothing persisted).
export const previewLoyaltyCampaignAudience = async (
  storeId: string,
  audience: CampaignAudience,
): Promise<CampaignAudiencePreview> => {
  const raw = (await httpClient.json.post(
    `${campaignsUrl(storeId)}/audience-preview`,
    { audience },
  )) as Partial<CampaignAudiencePreview> | null
  return {
    total: raw?.total ?? 0,
    email_eligible: raw?.email_eligible ?? 0,
    sms_eligible: raw?.sms_eligible ?? 0,
  }
}

// Server render of the fixed layout with sample tokens — the builder's live
// sidebar. The same renderer runs at send time, so the preview is literally
// what will be delivered (no client-side token drift).
export const previewLoyaltyCampaign = async (
  storeId: string,
  input: PreviewCampaignInput,
): Promise<PreviewCampaignResult> => {
  const raw = (await httpClient.json.post(
    `${campaignsUrl(storeId)}/preview`,
    input,
  )) as Partial<PreviewCampaignResult> | null
  return { subject: raw?.subject ?? '', html: raw?.html ?? '' }
}

// "Test Before Sending" (review step): mails ONE copy of the builder's current
// content to a single address. Content travels in the body exactly like
// /preview, so a campaign that has never been saved can still be tested. The
// server only enqueues the worker task — the resolved value just confirms the
// queueing, it is not a delivery receipt.
export const testSendLoyaltyCampaign = async (
  storeId: string,
  input: TestSendCampaignInput,
): Promise<TestSendCampaignResult> => {
  const raw = (await httpClient.json.post(
    `${campaignsUrl(storeId)}/test-send`,
    input,
  )) as Partial<TestSendCampaignResult> | null
  return {
    queued: raw?.queued ?? false,
    to: raw?.to ?? input.to,
    channel: raw?.channel ?? 'email',
  }
}

// The store's Pantry-theme brand logo and colour: the builder's "Use store
// logo" value / the server's fallback when logo_url is null, and the block
// editor's default brand colour. The API returns null when the store has no
// usable logo or colour; both are normalised to '' for the UI.
export const getLoyaltyBrandLogo = async (
  storeId: string,
): Promise<LoyaltyBrand> => {
  const raw = (await httpClient.json.get(
    `${campaignsUrl(storeId)}/brand-logo`,
  )) as Partial<BrandLogoResponse> | null
  return {
    brandLogoUrl: raw?.brand_logo_url ?? '',
    brandColor: raw?.brand_color ?? '',
    siteBase: raw?.site_base ?? '',
  }
}

export const sendLoyaltyCampaign = async (
  storeId: string,
  campaignId: string,
): Promise<LoyaltyCampaign> => {
  const raw = (await httpClient.json.post(
    `${campaignsUrl(storeId)}/${campaignId}/send`,
  )) as { campaign: LoyaltyCampaign }
  return raw.campaign
}

// Lifecycle transition (schedule / pause / resume / revert-to-draft). The
// server validates the transition against the campaign's current status.
export const updateLoyaltyCampaign = async (
  storeId: string,
  campaignId: string,
  input: UpdateCampaignInput,
): Promise<LoyaltyCampaign> => {
  const raw = (await httpClient.json.patch(
    `${campaignsUrl(storeId)}/${campaignId}`,
    input,
  )) as { campaign: LoyaltyCampaign }
  return raw.campaign
}

// Append-only lifecycle audit log (created / status_changed / send_*), newest
// first, cursor-paginated by `created_at` via `before`/`next_cursor` — same
// pagination contract as the flow history feed.
export const getLoyaltyCampaignHistory = async (
  storeId: string,
  campaignId: string,
  params: LoyaltyFlowHistoryParams = {},
): Promise<LoyaltyCampaignHistoryResponse> => {
  const raw = (await httpClient.json.get(
    `${campaignsUrl(storeId)}/${campaignId}/history`,
    {
      params: {
        ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
        ...(params.before ? { before: params.before } : {}),
      },
    },
  )) as LoyaltyCampaignHistoryResponse | null
  return {
    events: raw?.events ?? [],
    next_cursor: raw?.next_cursor ?? null,
  }
}

const campaignReportUrl = (storeId: string, campaignId: string) =>
  `${campaignsUrl(storeId)}/${campaignId}/report`

export const getCampaignReport = async (
  storeId: string,
  campaignId: string,
): Promise<CampaignReportSummary> =>
  (await httpClient.json.get(
    campaignReportUrl(storeId, campaignId),
  )) as CampaignReportSummary

export const getCampaignReportRecipients = async (
  storeId: string,
  campaignId: string,
  { all, ...params }: CampaignReportRecipientsParams,
): Promise<CampaignReportRecipientsResponse> =>
  (await httpClient.json.get(
    `${campaignReportUrl(storeId, campaignId)}/recipients`,
    { params: { ...params, ...(all ? { all: 1 } : {}) } },
  )) as CampaignReportRecipientsResponse

export const getCampaignReportRecipientDetail = async (
  storeId: string,
  campaignId: string,
  recipient: { email: string; customer_id: string },
): Promise<CampaignReportRecipientDetail> =>
  (await httpClient.json.get(
    `${campaignReportUrl(storeId, campaignId)}/recipients/detail`,
    { params: recipient },
  )) as CampaignReportRecipientDetail

export const getCampaignReportOverview = async (
  storeId: string,
  campaignId: string,
): Promise<CampaignReportOverview> =>
  (await httpClient.json.get(
    `${campaignReportUrl(storeId, campaignId)}/overview`,
  )) as CampaignReportOverview

export const getCampaignReportActivity = async (
  storeId: string,
  campaignId: string,
  params: { type?: CampaignReportActivityType; offset: number; limit: number },
): Promise<CampaignReportActivityResponse> =>
  (await httpClient.json.get(
    `${campaignReportUrl(storeId, campaignId)}/activity`,
    { params },
  )) as CampaignReportActivityResponse

export const deleteLoyaltyCampaign = async (
  storeId: string,
  campaignId: string,
): Promise<{ deleted: boolean }> =>
  (await httpClient.json.delete(`${campaignsUrl(storeId)}/${campaignId}`)) as {
    deleted: boolean
  }

// ── Flows ────────────────────────────────────────────────────────────────────

export const getLoyaltyFlows = async (
  storeId: string,
): Promise<LoyaltyFlow[]> => {
  const raw = (await httpClient.json.get(flowsUrl(storeId))) as {
    flows?: LoyaltyFlow[]
  } | null
  return raw?.flows ?? []
}

export const createLoyaltyFlow = async (
  storeId: string,
  input: CreateFlowInput,
): Promise<LoyaltyFlow> => {
  const raw = (await httpClient.json.post(flowsUrl(storeId), input)) as {
    flow: LoyaltyFlow
  }
  return raw.flow
}

export const updateLoyaltyFlow = async (
  storeId: string,
  flowId: string,
  input: UpdateFlowInput,
): Promise<LoyaltyFlow> => {
  const raw = (await httpClient.json.patch(
    `${flowsUrl(storeId)}/${flowId}`,
    input,
  )) as { flow: LoyaltyFlow }
  return raw.flow
}

export const deleteLoyaltyFlow = async (
  storeId: string,
  flowId: string,
): Promise<{ deleted: boolean }> =>
  (await httpClient.json.delete(`${flowsUrl(storeId)}/${flowId}`)) as {
    deleted: boolean
  }

// The customers a flow acted on, most recently active first (Activity ›
// Customer view). `search` matches the email, customer id or name.
export const getLoyaltyFlowCustomers = async (
  storeId: string,
  flowId: string,
  params: LoyaltyFlowCustomersParams = {},
): Promise<LoyaltyFlowCustomersResponse> => {
  const raw = (await httpClient.json.get(
    `${flowsUrl(storeId)}/${flowId}/customers`,
    {
      params: {
        ...(params.search ? { search: params.search } : {}),
        ...(typeof params.offset === 'number' ? { offset: params.offset } : {}),
        ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
      },
    },
  )) as LoyaltyFlowCustomersResponse | null
  return {
    customers: raw?.customers ?? [],
    next_offset: raw?.next_offset ?? null,
  }
}

// One customer's activity in this flow, plus their cart.
export const getLoyaltyFlowCustomerDetail = async (
  storeId: string,
  flowId: string,
  customer: LoyaltyFlowCustomerIdentity,
): Promise<LoyaltyFlowCustomerDetail> =>
  (await httpClient.json.get(
    `${flowsUrl(storeId)}/${flowId}/customers/detail`,
    {
      params: {
        ...(customer.customer_id ? { customer_id: customer.customer_id } : {}),
        ...(customer.email ? { email: customer.email } : {}),
      },
    },
  )) as LoyaltyFlowCustomerDetail

// Append-only audit log of a flow's triggers (scheduled → sent/failed/skipped),
// newest-first and cursor-paginated by `created_at` via `before`/`next_cursor`.
export const getLoyaltyFlowHistory = async (
  storeId: string,
  flowId: string,
  params: LoyaltyFlowHistoryParams = {},
): Promise<LoyaltyFlowHistoryResponse> => {
  const raw = (await httpClient.json.get(
    `${flowsUrl(storeId)}/${flowId}/history`,
    {
      params: {
        ...(typeof params.limit === 'number' ? { limit: params.limit } : {}),
        ...(params.before ? { before: params.before } : {}),
      },
    },
  )) as LoyaltyFlowHistoryResponse | null
  return {
    events: raw?.events ?? [],
    next_cursor: raw?.next_cursor ?? null,
  }
}
